"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HumanReadableTab = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
// Calculate PGN rate per second using moving average from history
const calculatePgnRate = (current, history) => {
    if (!history || history.length < 2) {
        return null;
    }
    // Build array of all messages including current, sorted by timestamp (newest first)
    const allMessages = [current, ...history].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Calculate intervals between consecutive messages (in seconds)
    const intervals = [];
    for (let i = 0; i < allMessages.length - 1; i++) {
        const currentTime = new Date(allMessages[i].timestamp).getTime();
        const nextTime = new Date(allMessages[i + 1].timestamp).getTime();
        const intervalSeconds = (currentTime - nextTime) / 1000;
        if (intervalSeconds > 0) {
            intervals.push(intervalSeconds);
        }
    }
    if (intervals.length === 0) {
        return null;
    }
    // Calculate moving average of the intervals
    // Use exponential moving average for smoother results
    const alpha = 0.3; // Smoothing factor (0.1 = more smoothing, 0.9 = less smoothing)
    let movingAvgInterval = intervals[0];
    for (let i = 1; i < intervals.length; i++) {
        movingAvgInterval = alpha * intervals[i] + (1 - alpha) * movingAvgInterval;
    }
    // Rate = 1 / average interval (messages per second)
    const rate = 1 / movingAvgInterval;
    return parseFloat(rate.toFixed(1));
};
const HumanReadableTab = ({ pgnData, definition, pgnHistory = [] }) => {
    const rate = calculatePgnRate(pgnData, pgnHistory);
    if (!pgnData.fields) {
        return (0, jsx_runtime_1.jsx)("div", { children: "No field data available" });
    }
    // Helper function to format field values in a human-readable way
    const formatFieldValue = (value, field) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        // Handle special value formatting
        if (typeof value === 'number') {
            // Check if it's a special "not available" value
            if (value === 0xffff || value === 0xff || value === 0xffffffff) {
                return 'Not Available';
            }
            // Format numbers with appropriate precision
            if (field?.Unit) {
                if (value % 1 === 0) {
                    return `${value} ${field.Unit}`;
                }
                else {
                    return `${value.toFixed(3)} ${field.Unit}`;
                }
            }
            return value.toString();
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (Array.isArray(value)) {
            return value.join(', ');
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    };
    // Helper function to get field definition by ID
    const getFieldDefinition = (fieldId) => {
        if (!definition?.Fields)
            return undefined;
        return definition.Fields.find((f) => f.Id === fieldId || f.Name === fieldId);
    };
    // Helper function to get a human-readable field name
    const getFieldDisplayName = (fieldId, fieldDef) => {
        if (fieldDef?.Name) {
            return fieldDef.Name;
        }
        return fieldId;
    };
    // Render regular fields
    const renderFields = () => {
        const fieldEntries = Object.entries(pgnData.fields);
        if (fieldEntries.length === 0) {
            return (0, jsx_runtime_1.jsx)("div", { children: "No field data available" });
        }
        return ((0, jsx_runtime_1.jsx)("div", { children: fieldEntries.map(([fieldId, value]) => {
                // Skip special fields that are not actual data fields
                if (fieldId === 'list' || fieldId === 'timestamp' || fieldId === 'prio') {
                    return null;
                }
                const fieldDef = getFieldDefinition(fieldId);
                const displayName = getFieldDisplayName(fieldId, fieldDef);
                const formattedValue = formatFieldValue(value, fieldDef);
                return ((0, jsx_runtime_1.jsx)("div", { style: {
                        marginBottom: '6px',
                        padding: '6px 8px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '3px',
                    }, children: (0, jsx_runtime_1.jsxs)("div", { style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                        }, children: [(0, jsx_runtime_1.jsxs)("div", { style: { flex: '1 1 200px', marginRight: '8px' }, children: [(0, jsx_runtime_1.jsx)("strong", { style: { color: '#495057', fontSize: '12px' }, children: displayName }), fieldDef?.Description && ((0, jsx_runtime_1.jsx)("div", { style: {
                                            fontSize: '10px',
                                            color: '#6c757d',
                                            marginTop: '1px',
                                            fontStyle: 'italic',
                                            lineHeight: '1.2',
                                        }, children: fieldDef.Description }))] }), (0, jsx_runtime_1.jsx)("div", { style: {
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#212529',
                                    flex: '0 0 auto',
                                }, children: formattedValue })] }) }, fieldId));
            }) }));
    };
    // Render repeating/list fields
    const renderListFields = () => {
        const listData = pgnData.fields.list;
        if (!listData || !Array.isArray(listData) || listData.length === 0) {
            return null;
        }
        return ((0, jsx_runtime_1.jsxs)("div", { style: { marginTop: '15px' }, children: [(0, jsx_runtime_1.jsxs)("h6", { style: { marginBottom: '10px', color: '#495057', fontSize: '13px' }, children: ["Repeating Data (", listData.length, " entries)"] }), listData.map((item, index) => ((0, jsx_runtime_1.jsxs)("div", { style: {
                        marginBottom: '10px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: '#ffffff',
                    }, children: [(0, jsx_runtime_1.jsxs)("div", { style: {
                                backgroundColor: '#e9ecef',
                                padding: '5px 8px',
                                borderBottom: '1px solid #dee2e6',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                color: '#495057',
                            }, children: ["Entry #", index + 1] }), (0, jsx_runtime_1.jsx)("div", { style: { padding: '6px' }, children: Object.entries(item).map(([fieldId, value]) => {
                                const fieldDef = getFieldDefinition(fieldId);
                                const displayName = getFieldDisplayName(fieldId, fieldDef);
                                const formattedValue = formatFieldValue(value, fieldDef);
                                return ((0, jsx_runtime_1.jsxs)("div", { style: {
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '3px 0',
                                        borderBottom: '1px solid #f8f9fa',
                                        fontSize: '11px',
                                    }, children: [(0, jsx_runtime_1.jsxs)("span", { style: { fontWeight: '500', color: '#495057' }, children: [displayName, ":"] }), (0, jsx_runtime_1.jsx)("span", { style: { color: '#212529' }, children: formattedValue })] }, `${index}-${fieldId}`));
                            }) })] }, index)))] }));
    };
    return ((0, jsx_runtime_1.jsxs)("div", { style: { padding: '6px' }, children: [(0, jsx_runtime_1.jsxs)("div", { style: { marginBottom: '12px' }, children: [(0, jsx_runtime_1.jsx)("h5", { style: { color: '#495057', marginBottom: '8px', fontSize: '14px' }, children: pgnData.description || `PGN ${pgnData.pgn}` }), (0, jsx_runtime_1.jsxs)("div", { style: {
                            fontSize: '11px',
                            color: '#6c757d',
                            backgroundColor: '#e9ecef',
                            padding: '5px 8px',
                            borderRadius: '3px',
                            marginBottom: '12px',
                            lineHeight: '1.3',
                        }, children: [(0, jsx_runtime_1.jsx)("strong", { children: "Source:" }), " ", pgnData.src, " | ", (0, jsx_runtime_1.jsx)("strong", { children: "Destination:" }), " ", pgnData.dst || 'N/A', " |", ' ', (0, jsx_runtime_1.jsx)("strong", { children: "Priority:" }), " ", pgnData.fields?.prio || 'N/A', pgnData.fields?.timestamp && ((0, jsx_runtime_1.jsxs)("span", { children: [' ', "| ", (0, jsx_runtime_1.jsx)("strong", { children: "Timestamp:" }), " ", new Date(pgnData.fields.timestamp).toLocaleString()] })), rate !== null && ((0, jsx_runtime_1.jsxs)("span", { children: [' ', "| ", (0, jsx_runtime_1.jsx)("strong", { children: "Rate:" }), " ", rate, " PGNs/sec", ' '] }))] })] }), renderFields(), renderListFields(), definition?.Explanation && ((0, jsx_runtime_1.jsxs)("div", { style: {
                    marginTop: '20px',
                    padding: '10px',
                    backgroundColor: '#f1f8ff',
                    border: '1px solid #c1d7f0',
                    borderRadius: '4px',
                }, children: [(0, jsx_runtime_1.jsx)("h6", { style: {
                            color: '#495057',
                            fontSize: '12px',
                            marginBottom: '6px',
                            fontWeight: 'bold',
                        }, children: "PGN Explanation" }), (0, jsx_runtime_1.jsx)("div", { style: {
                            fontSize: '11px',
                            color: '#495057',
                            lineHeight: '1.4',
                            textAlign: 'justify',
                        }, children: definition.Explanation })] }))] }));
};
exports.HumanReadableTab = HumanReadableTab;
